/* eslint-disable no-underscore-dangle */
/* eslint-disable no-undef */
/* eslint-disable no-param-reassign */
import { includes, isEmpty, isFinite, set } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';

import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';

import { POINT_TYPES } from '../../api/Styles/Styles';

import adaptURL from '../adapt-url';
import checkUTFGridInURL from '../check-utfgrid-in-url';
import { coordinatesToXYZ } from '../coordinates-to-xyz';
import { scaleIconSize } from '../geojson-layer-style';
import getGeoJSONFromMapBox from '../get-geojson-from-mapbox';
import getUTFGridDataURL from '../get-utfgrid-data-url';
import loadTMSBBoxFromURL from '../load-tms-bbox-from-url';
import loadWMSBBoxFromURL from '../load-wms-bbox-from-url';

import addPopupToMapBoxGL from './add-popup-to-mapbox-gl';
import buildGeoImageLayer from './buildGeoImageLayer';
import buildGeoJSONLayer from './buildGeoJSONLayer';
import buildGeoJSONVTLayer from './buildGeoJSONVTLayer';
import buildGeoTiffLayer from './buildGeoTiffLayer';
import buildIndoorGeoJSONLayer from './buildIndoorGeoJSONLayer';
import buildMapBoxGLLayer from './buildMapBoxGLLayer';
import buildMapBoxLayer from './buildMapBoxLayer';
import buildRealTimeLayer from './buildRealTimeLayer';
import buildTileServerLayer from './buildTileServerLayer';
import buildTMSLayer from './buildTMSLayer';
import buildTopoJSONLayer from './buildTopoJSONLayer';
import buildUTFGridLayer from './buildUTFGridLayer';
import buildWMSLayer from './buildWMSLayer';
import buildWMTSLayer from './buildWMTSLayer';

const REGEX = /mobile/i;
const ARBITRARY_MAX_ZOOM = 20;

/**
 * Draws a single layer in a leaflet map.
 *
 * @async
 * @param {*} arg - The object argument.
 * @param {object} arg.layer - The layer object to draw.
 * @param {object} arg.style - The style of the layer to draw.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {object} arg.layerToDraw - The leaflet layer object.
 * @param {boolean} [arg.isUpdate=false] - A flag to check if is an update call or not.
 * @param {boolean} [arg.isThumbnailMap=false] - A flag to check if layer is for a thumbnail.
 * @param {boolean} [arg.zoomLevel=false] - A flag to check if zoom should be checked.
 * @returns {object} The leaflet layer object added to the map.
 */
const drawSingleLayer = async ({
  layer,
  style,
  leafletMap,
  layerToDraw,
  isUpdate = false,
  isThumbnailMap = false,
  zoomLevel = false
}) => {
  let newLayerToDraw = '';
  let bbox = [];

  if (isUpdate && !isEmpty(layerToDraw)) leafletMap.removeLayer(layerToDraw);

  const zIndex = 10;
  leafletMap.createPane('10');

  try {
    switch (true) {
      case layerOps.isGeoImage(layer):
        newLayerToDraw = buildGeoImageLayer({ layer, style, zIndex });
        break;
      case layerOps.isGeoTiff(layer):
        newLayerToDraw = await buildGeoTiffLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isGeoJSONApiBased(layer):
        newLayerToDraw = await buildGeoJSONLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isGeoJSONVT(layer):
        newLayerToDraw = await buildGeoJSONVTLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isIndoor(layer):
        newLayerToDraw = await buildIndoorGeoJSONLayer({
          layer,
          style,
          zIndex,
          leafletMap,
          isPartOfMap: true,
          zoomLevel
        });
        break;
      case layerOps.isMapBox(layer):
        newLayerToDraw = await buildMapBoxLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isMapBoxGL(layer):
        newLayerToDraw = await buildMapBoxGLLayer({ layer, style, zIndex });
        break;
      case layerOps.isRealTime(layer):
      case layerOps.isAnnotationsRealTime(layer):
        newLayerToDraw = await buildRealTimeLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isTileServer(layer):
        newLayerToDraw = buildTileServerLayer({ layer, style, zIndex });
        break;
      case layerOps.isTMS(layer):
        newLayerToDraw = buildTMSLayer({ layer, style, zIndex });
        break;
      case layerOps.isTopoJSON(layer):
        newLayerToDraw = await buildTopoJSONLayer({ layer, style, zIndex, isPartOfMap: true });
        break;
      case layerOps.isWMS(layer):
        newLayerToDraw = buildWMSLayer({ layer, style, zIndex });
        break;
      case layerOps.isWMTS(layer):
        newLayerToDraw = buildWMTSLayer({ layer, style, zIndex });
        break;
      default:
        Bert.alert('Wrong Layer data', 'danger');
        break;
    }

    if (isThumbnailMap) {
      if (leafletMap.attributionControl) leafletMap.removeControl(leafletMap.attributionControl);
      if (leafletMap.zoomControl) set(leafletMap, 'zoomControl._container.hidden', true);
    }

    if (
      navigator.userAgent.match(REGEX) &&
      (layerOps.isGeoJSONVT(layer) || layerOps.isMapBox(layer))
    )
      leafletMap.on('zoomanim', () => newLayerToDraw.redraw());

    if (
      styleOps.isScaledSymbology(style) &&
      (includes([POINT_TYPES.ICON, POINT_TYPES.MARKER], styleOps.getPointType(style)) ||
        styleOps.isCustom(style))
    ) {
      let fromZoom = leafletMap.getZoom();
      const maxZoom = isFinite(leafletMap.getMaxZoom())
        ? leafletMap.getMaxZoom()
        : ARBITRARY_MAX_ZOOM;
      const initialScale = leafletMap.getZoomScale(fromZoom, maxZoom);
      leafletMap.on('zoomstart', event => {
        fromZoom = event.target.getZoom();
      });
      const isMarker = styleOps.getPointType(style) === POINT_TYPES.MARKER;
      scaleIconSize({
        newLayerToDraw,
        scale: initialScale,
        isMarker,
        isCustom: styleOps.isCustom(style)
      });
      leafletMap.on('zoomend', () => {
        const toZoom = leafletMap.getZoom();
        const scale = leafletMap.getZoomScale(toZoom, fromZoom);
        scaleIconSize({ newLayerToDraw, scale, isMarker, isCustom: styleOps.isCustom(style) });
      });
    }

    if (newLayerToDraw.options.maxZoom !== layerOps.getMaxZoom(layer)) {
      newLayerToDraw.options.minZoom = layerOps.getMinZoom(layer);
      newLayerToDraw.options.maxZoom = layerOps.getMaxZoom(layer);
    }
    leafletMap.addLayer(newLayerToDraw);

    if (!layerOps.isIndoor(layer) && zoomLevel)
      leafletMap.on('zoomend', event => {
        const zoom = event.target.getZoom();
        if (!layerOps.isZoomValid(layer, zoom)) leafletMap.removeLayer(newLayerToDraw);
        else if (!leafletMap.hasLayer(newLayerToDraw)) leafletMap.addLayer(newLayerToDraw);
      });

    switch (true) {
      case layerOps.isWMS(layer):
        bbox = await loadWMSBBoxFromURL({
          url: layerOps.getURL(layer),
          wmsLayerNames: layerOps.getWMSLayerNames(layer),
          connectionParams: layerOps.getConnectionParams(layer)
        });
        break;
      case layerOps.isTMS(layer):
        bbox = await loadTMSBBoxFromURL(layerOps.getURL(layer));
        break;
      case layerOps.isMapBoxGL(layer):
        addPopupToMapBoxGL({ newLayerToDraw, layer, style, leafletMap });
        break;
      default:
        break;
    }

    if (layerOps.isGeoImage(layer))
      leafletMap.fitBounds([
        [layerOps.getTopRight(layer).lat, layerOps.getTopRight(layer).lng],
        [layerOps.getBottomLeft(layer).lat, layerOps.getBottomLeft(layer).lng]
      ]);

    if (layerOps.isGeoTiff(layer) || layerOps.isTopoJSON(layer))
      leafletMap.fitBounds(newLayerToDraw.getBounds());

    if (layerOps.isWMS(layer) || layerOps.isTMS(layer)) {
      if (isEmpty(layerOps.getMapLocation(layer)) && !isEmpty(bbox))
        leafletMap.fitBounds([
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]]
        ]);

      const urlUTFGrid = getUTFGridDataURL({ layer, bbox });
      const utfGridExists = await checkUTFGridInURL(
        urlUTFGrid,
        layerOps.getConnectionParams(layer)
      );
      if (utfGridExists) {
        const utfLayer = await buildUTFGridLayer(layer);
        leafletMap.addLayer(utfLayer);
      }
    }

    if (layerOps.isMapBox(layer) && isEmpty(layerOps.getMapLocation(layer))) {
      const defaultMapCenter = isEmpty(layerOps.getMapLocation(layer))
        ? turf.point([0, 0], { zoom: 0 })
        : layerOps.getMapLocation(layer);
      const layerTileParams = coordinatesToXYZ(defaultMapCenter);
      const geojson = await getGeoJSONFromMapBox({
        layerTileParams,
        mapboxLayerName: layerOps.getMapBoxLayerName(layer),
        url: adaptURL(layerOps.getURL(layer))
      });
      bbox = turf.bbox(geojson);
      leafletMap.fitBounds([
        [bbox[3], bbox[2]],
        [bbox[1], bbox[0]]
      ]);
    }
  } catch (error) {
    console.error(error);
  }
  return newLayerToDraw;
};
export default drawSingleLayer;
